import React, { useState } from "react";
import useTranslator from "../../../hooks/useTranslator";
import { getCurrentDate } from "../../../helpers/Utils";
import DateRangeFilter from "../../../components/shared/fields/DateRangeFilter";
import DashboardSection from "../../dashboard/components/DashboardSection";
import "../../dashboard/components/dashboard-sections.css";
import { CycleIconSvg } from "../../../svgFiles/CycleIconSvg";
import { TrendingUpIconSvg } from "../../../svgFiles/TrendingUpIconSvg";
import { PackageIconSvg } from "../../../svgFiles/PackageIconSvg";
import { UsersIconSvg } from "../../../svgFiles/UsersIconSvg";
const BrokerageDashboard = () => {
  const { _t } = useTranslator();  
  const currentDate = getCurrentDate("YYYY-MM-DD");
  const [date, setDate] = useState(currentDate);
  const [, setStartDate] = useState(currentDate);
  const [, setEndDate] = useState(currentDate);

  const handleApplyRange = ({ startDate: start, endDate: end }) => {
    setStartDate(start);
    setEndDate(end);
    setDate(start);
  };

  // Running Totals Items
  const runningTotalsItems = [
    { label: _t("total_paid", "Total Paid"), value: 0, unit: " KG" },
    { label: _t("total_received", "Total Received"), value: 0, unit: " KG" },
  ];

  // Processing Items - with nested structure
  const processingItems = [
    {
      title: _t("prime", "Prime"),
      total: 0,
      unit: " KG",
      details: [
        { label: "Cheeks", value: 0, unit: " KG" },
        { label: "Strips", value: 0, unit: " KG" },
      ]
    },
    {
      title: _t("second", "Second"),
      total: 0,
      unit: " KG",
      details: [
        { label: "Cheeks", value: 0, unit: " KG" },
        { label: "Strips", value: 0, unit: " KG" },
      ]
    },
    {
      title: _t("pieces", "Pieces"),
      total: 0,
      unit: " KG",
      details: null
    },
    {
      title: _t("pasting", "Pasting"),
      total: 0,
      unit: " KG",
      details: null
    },
  ];

  // Sorting Items
  const sortingTopItems = [
    { label: _t("total_sorted", "Total Sorted"), value: 0, unit: " KG" },
    { label: _t("rejected", "Rejected"), value: 0, unit: " KG" },
  ];

  const sortingBottomItems = [
    { label: _t("prime_cheeks_boxes_10", "Prime Cheeks boxes(10)"), value: 0, category: "Prime" },
    { label: _t("prime_cheeks_boxes_20", "Prime Cheeks boxes(20)"), value: 0, category: "Prime" },
    { label: _t("prime_strips_boxes_20", "Prime Strips boxes(20)"), value: 0, category: "Prime" },
    { label: _t("second_cheeks_boxes_10", "Second Cheeks boxes(10)"), value: 0, category: "Second" },
    { label: _t("second_cheeks_boxes_20", "Second Cheeks boxes(20)"), value: 0, category: "Second" },
    { label: _t("second_strips_boxes_20", "Second Strips boxes(20)"), value: 0, category: "Second" },
    { label: _t("pieces_boxes_20", "Pieces boxes(20)"), value: 0, category: "Pieces" },
    { label: _t("pasting_boxes", "Pasting boxes"), value: 0, category: "Pasting" },
  ];

  // Day/Night Separation Items
  const separationItems = [
    {
      title: _t("day_separation", "Day Separation"),
      items: [
        { label: _t("strips_kg", "Strips KG"), value: 0, unit: " KG" },
        { label: _t("cheeks_kg", "Cheeks KG"), value: 0, unit: " KG" },
        { label: _t("number_of_people_separating", "Number of People Separating"), value: 0, unit: "" },
      ]
    },
    {
      title: _t("night_separation", "Night Separation"),
      items: [
        { label: _t("strips_kg", "Strips KG"), value: 0, unit: " KG" },
        { label: _t("cheeks_kg", "Cheeks KG"), value: 0, unit: " KG" },
        { label: _t("number_of_people_separating", "Number of People Separating"), value: 0, unit: "" },
      ]
    },
  ];

  return (
    <div>
      {/* ── Toolbar with Date Filter ──────────────────────────── */}
      <div className="receiving_dashboard_toolbar align-items-center">
        <h5 className="receiving_dashboard_toolbar_title m-0">
          {_t("brokerage_overview", "Brokerage Overview")}
        </h5>
        <div className="receiving_filter_group">          
          <DateRangeFilter setDate={setDate} onApplyRange={handleApplyRange} triggerId="brokerage-date-range-filter" />          
        </div>
      </div>

      <div>

        {/* ── Running Totals Section ── */}
        <DashboardSection
          title={
            <span className="d-flex align-items-center gap-2">
              <TrendingUpIconSvg size={18} color="#4ade80" />
              {_t("running_totals_session", "Running totals (session)")}
            </span>
          }
        >
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "12px" }}>
            {runningTotalsItems.map((item, i) => (
              <div
                key={i}
                style={{
                  backgroundColor: "#f9fafb",
                  border: "1px solid #f0f1f3",
                  borderRadius: "8px",
                  padding: "16px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                }}
              >
                <div style={{ fontSize: "11px", fontWeight: "600", color: "#667085", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  {item.label}
                </div>
                <div style={{ fontSize: "20px", fontWeight: "700", color: "#4ade80" }}>
                  {item.value}{item.unit}
                </div>
              </div>
            ))}
          </div>
        </DashboardSection>

        {/* ── Processing Section ── */}
        <DashboardSection
          title={
            <span className="d-flex align-items-center gap-2">
              <CycleIconSvg size={18} color="#60a5fa" />
              {_t("processing_overview", "Processing Overview")}
            </span>
          }
        >
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px" }}>
            {processingItems.map((item, i) => (
              <div key={i} style={{ border: "1px solid #f0f1f3", borderRadius: "8px", padding: "16px", backgroundColor: "#f9fafb" }}>
                <div style={{ fontSize: "12px", fontWeight: "600", color: "#1d1f2c", marginBottom: "12px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  {item.title}
                </div>
                <div style={{ fontSize: "24px", fontWeight: "700", color: "#60a5fa", marginBottom: item.details ? "12px" : "0" }}>
                  {item.total}{item.unit}
                </div>
                {item.details && (
                  <div style={{ display: "flex", flexDirection: "column", gap: "10px", paddingTop: "12px", borderTop: "1px solid #e5e7eb" }}>
                    {item.details.map((detail, j) => (
                      <div key={j} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: "12px", color: "#667085" }}>
                          {detail.label}
                        </span>
                        <span style={{ fontSize: "14px", fontWeight: "600", color: "#1d1f2c" }}>
                          {detail.value}{detail.unit || ""}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </DashboardSection>

        

        {/* ── Sorting Section ── */}
        <DashboardSection
          title={
            <span className="d-flex align-items-center gap-2">
              <PackageIconSvg size={18} color="#fb923c" />
              {_t("sorting", "Sorting")}
            </span>
          }
        >
          {/* Top Metrics */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "12px", marginBottom: "24px" }}>
            {sortingTopItems.map((item, i) => (
              <div
                key={i}
                style={{
                  backgroundColor: "#f9fafb",
                  border: "1px solid #f0f1f3",
                  borderRadius: "8px",
                  padding: "16px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  transition: "all 0.2s ease",
                }}
              >
                <div style={{ fontSize: "11px", fontWeight: "600", color: "#667085", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  {item.label}
                </div>
                <div style={{ fontSize: "22px", fontWeight: "700", color: "#fb923c" }}>
                  {item.value}{item.unit}
                </div>
              </div>
            ))}
          </div>

          {/* Category List */}
          <div >
            {sortingBottomItems.reduce((acc, item, idx) => {
              const lastCategory = acc.length > 0 ? acc[acc.length - 1].category : null;
              if (item.category !== lastCategory) {
                acc.push({ type: "header", category: item.category, key: `header-${item.category}` });
              }
              acc.push({ ...item, type: "row", key: idx });
              return acc;
            }, []).map((element, i) => (
              element.type === "header" ? (
                <div key={element.key} style={{ 
                  marginTop: i > 0 ? "18px" : "0", 
                  paddingTop: "10px", 
                  paddingBottom: "10px", 
                  paddingLeft: "10px",
                  fontWeight: "700", 
                  fontSize: "11px", 
                  color: "#fb923c", 
                  textTransform: "uppercase", 
                  letterSpacing: "0.7px",
                  borderLeft: "3px solid #fb923c",
                  borderTop: i > 0 ? "1px solid #f0f1f3" : "none", 
                  backgroundColor: "#f9fafb"
                }}>
                  {element.category}
                </div>
              ) : (
                <div key={element.key} style={{ 
                  display: "flex", 
                  justifyContent: "space-between", 
                  alignItems: "center", 
                  padding: "11px 10px", 
                  transition: "background-color 0.15s ease"
                }}>
                  <span style={{ fontSize: "13px", color: "#667085", fontWeight: "500" }}>{_t(element.label, element.label)}</span>
                  <span style={{ fontSize: "16px", fontWeight: "700", color: "#fb923c" }}>{element.value}</span>
                </div>
              )
            ))}
          </div>
        </DashboardSection>
        {/* ── Day/Night Separation Section ── */}
        <DashboardSection
          title={
            <span className="d-flex align-items-center gap-2">
              <UsersIconSvg size={18} color="#60a5fa" />
              {_t("day_night_separation", "Day/Night Separation")}
            </span>
          }
        >
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "16px" }}>
            {separationItems.map((section, i) => (
              <div key={i} style={{ border: "1px solid #f0f1f3", borderRadius: "8px", padding: "16px", backgroundColor: "#f9fafb" }}>
                <div style={{ fontSize: "12px", fontWeight: "600", color: "#1d1f2c", marginBottom: "16px", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                  {section.title}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                  {section.items.map((item, j) => (
                    <div key={j} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: "12px", color: "#667085" }}>
                        {item.label}
                      </span>
                      <span style={{ fontSize: "14px", fontWeight: "600", color: "#1d1f2c" }}>
                        {item.value}{item.unit}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </DashboardSection>
      </div>
    </div>
  );
};

export default BrokerageDashboard;
